import { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Collapse,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { Hypothesis, Report } from '../types';

interface ReportCardProps {
  report: Report;
  defaultExpanded?: boolean;
  hideFinalSections?: boolean;
  finalOnly?: boolean;
  onWriteConclusion?: (hypothesis: Hypothesis) => void;
  writingConclusion?: boolean;
  refinements?: string[];
  onRequestTreatment?: (hypothesis: Hypothesis, index: number) => Promise<void>;
  loadingTreatmentIndex?: number | null;
}

export default function ReportCard({ report, defaultExpanded = true, hideFinalSections = false, finalOnly = false, onWriteConclusion, writingConclusion = false, refinements = [], onRequestTreatment, loadingTreatmentIndex = null }: ReportCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [expandedTreatments, setExpandedTreatments] = useState<Set<number>>(new Set());

  const toggleTreatment = async (hypothesis: Hypothesis, index: number) => {
    const opening = !expandedTreatments.has(index);
    setExpandedTreatments((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
    const hasCalculatedMedication = hypothesis.medications?.some(
      (medication) => typeof medication !== 'string' && Boolean(medication.calculated_dose)
    );
    const hasTreatment = (hypothesis.treatment_plan?.length || 0) > 0 && Boolean(hasCalculatedMedication);
    if (opening && !hasTreatment && onRequestTreatment) await onRequestTreatment(hypothesis, index);
  };

  const sections: { key: keyof Report; title: string }[] = [
    { key: 'anamnesis', title: 'Анамнез' },
    { key: 'differential_diagnosis', title: 'Дифференциальный диагноз' },
  ];

  const renderHypotheses = () => {
    if (!Array.isArray(report.hypotheses) || report.hypotheses.length === 0) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" color="primary" gutterBottom>
          Гипотезы
        </Typography>
        {report.hypotheses.map((h, i) => (
          <Card key={i} variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {i + 1}. {h.name || 'Гипотеза'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Обоснование:</strong> {h.reasoning || '—'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Chip label={`Вероятность: ${h.probability || '—'}`} size="small" color="primary" variant="outlined" />
              <Chip label={`Срочность: ${h.urgency || '—'}`} size="small" color="secondary" variant="outlined" />
            </Box>
            <Typography variant="body2">
              <strong>Рекомендуемые исследования:</strong> {h.recommended_tests || '—'}
            </Typography>
            <Button
              sx={{ mt: 1.5 }}
              variant="outlined"
              size="small"
              onClick={() => void toggleTreatment(h, i)}
              disabled={loadingTreatmentIndex === i}
              startIcon={expandedTreatments.has(i) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {loadingTreatmentIndex === i ? 'Рассчитываю лечение…' : expandedTreatments.has(i) ? 'Скрыть лечение и препараты' : 'Показать лечение и препараты'}
            </Button>
            <Collapse in={expandedTreatments.has(i)}>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Предполагаемый процесс лечения</Typography>
                {Array.isArray(h.treatment_plan) && h.treatment_plan.length > 0 ? (
                  <List dense disablePadding>
                    {h.treatment_plan.map((step, stepIndex) => (
                      <ListItem key={stepIndex} sx={{ display: 'list-item', py: .25, pl: 2 }}>{step}</ListItem>
                    ))}
                  </List>
                ) : <Typography variant="body2">Не указан</Typography>}
                <Typography variant="subtitle2" sx={{ mt: 1.5 }} gutterBottom>Возможные препараты</Typography>
                {Array.isArray(h.medications) && h.medications.length > 0 ? (
                  <List dense disablePadding>
                    {h.medications.map((medication, medicationIndex) => (
                      <ListItem key={medicationIndex} sx={{ display: 'list-item', py: .5, pl: 2 }}>
                        {typeof medication === 'string' ? medication : (
                          <Box sx={{ width: '100%' }}>
                            <Typography variant="body2" fontWeight={600}>{medication.name || 'Препарат'}</Typography>
                            <Box sx={{ display: 'flex', gap: .75, flexWrap: 'wrap', my: .5 }}>
                              {medication.role && <Chip label={medication.role} size="small" />}
                              {medication.option_type && (
                                <Chip
                                  label={medication.option_type === 'alternative' ? 'Альтернатива' : medication.option_type === 'additional' ? 'Дополнительный' : 'Основной'}
                                  size="small"
                                  color={medication.option_type === 'alternative' ? 'secondary' : 'primary'}
                                  variant="outlined"
                                />
                              )}
                            </Box>
                            {medication.alternative_to && <Typography variant="body2"><strong>Заменяет:</strong> {medication.alternative_to} (не применять одновременно без отдельного назначения)</Typography>}
                            <Typography variant="body2">Цель: {medication.purpose || '—'}</Typography>
                            {medication.why_suitable && <Typography variant="body2">Почему подходит: {medication.why_suitable}</Typography>}
                            <Typography variant="body2">Доза: {medication.dose_mg_per_kg || '—'}</Typography>
                            <Typography variant="body2">Расчёт: {medication.calculated_dose || '—'}</Typography>
                            {medication.infusion_volume_calculation && <Typography variant="body2"><strong>Объём инфузии:</strong> {medication.infusion_volume_calculation}</Typography>}
                            {medication.infusion_rate_calculation && <Typography variant="body2"><strong>Скорость инфузии:</strong> {medication.infusion_rate_calculation}</Typography>}
                            <Typography variant="body2">Применение: {medication.route_frequency_duration || '—'}</Typography>
                            <Typography variant="body2">Противопоказания: {medication.contraindications || '—'}</Typography>
                            {medication.interactions && <Typography variant="body2">Совместимость и взаимодействия: {medication.interactions}</Typography>}
                            <Typography variant="body2">Контроль: {medication.monitoring || '—'}</Typography>
                            {medication.source_note && <Typography variant="caption" color="text.secondary">Проверить: {medication.source_note}</Typography>}
                          </Box>
                        )}
                      </ListItem>
                    ))}
                  </List>
                ) : <Typography variant="body2">Не указаны</Typography>}
                {Array.isArray(h.excluded_medications) && h.excluded_medications.length > 0 && (
                  <Box sx={{ mt: 2, p: 1.5, border: 1, borderColor: 'error.light', borderRadius: 1.5, bgcolor: 'background.paper' }}>
                    <Typography variant="subtitle2" color="error" gutterBottom>
                      Исключённые препараты — не применять
                    </Typography>
                    {h.excluded_medications.map((medication, excludedIndex) => (
                      <Box key={excludedIndex} sx={{ '&:not(:last-child)': { mb: 1.25 } }}>
                        <Typography variant="body2" fontWeight={600}>{medication.name || 'Препарат'}</Typography>
                        <Typography variant="body2">Причина исключения: {medication.reason || '—'}</Typography>
                        <Typography variant="body2">Более подходящая замена: {medication.safer_alternative || '—'}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
                  Схему и дозировки подтверждает ветеринар после осмотра и исследований.
                </Typography>
              </Box>
            </Collapse>
            {onWriteConclusion && (
              <Button sx={{ mt: 1.5 }} variant="contained" size="small" disabled={writingConclusion} onClick={() => onWriteConclusion(h)}>
                Написать заключение по этой гипотезе
              </Button>
            )}
          </Card>
        ))}
      </Box>
    );
  };

  const renderListSection = (title: string, items?: string[]) => {
    if (!items || items.length === 0) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" color="primary" gutterBottom>
          {title}
        </Typography>
        <List dense>
          {items.map((item, i) => (
            <ListItem key={i} sx={{ display: 'list-item', pl: 2 }}>
              {item}
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  const renderTextSection = (title: string, text?: string) => {
    if (!text) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" color="primary" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
          {text}
        </Typography>
      </Box>
    );
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        title={report.title || 'Заключение'}
        action={
          <Button
            size="small"
            onClick={() => setExpanded(!expanded)}
            startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            {expanded ? 'Свернуть' : 'Развернуть'}
          </Button>
        }
      />
      <Collapse in={expanded}>
        <CardContent>
          {!finalOnly && sections.map(({ key, title }) => renderTextSection(title, report[key] as string))}
          {!finalOnly && refinements.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" color="primary" gutterBottom>Уточнения</Typography>
              {refinements.map((item, index) => (
                <Box key={index} sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight={600}>#{index + 1}</Typography>
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>{item}</Typography>
                </Box>
              ))}
            </Box>
          )}
          {!finalOnly && renderHypotheses()}
          {!hideFinalSections && renderTextSection('Заключение', report.conclusion)}
          {!hideFinalSections && renderListSection('Рекомендации для владельца', report.owner_recommendations)}
          {!hideFinalSections && renderTextSection('Врачебные заметки', report.veterinarian_notes)}
          {!hideFinalSections && renderTextSection('Примечание', report.disclaimer)}
        </CardContent>
      </Collapse>
    </Card>
  );
}
